import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, LanguageProvider, useAuth, api } from './context/AppContext';
import { FeatureDiscoveryProvider } from './context/FeatureDiscoveryContext';
import OfflineIndicator from './components/OfflineIndicator';
import { Toaster } from './components/ui/sonner';
import { Loader2 } from 'lucide-react';
import { StaleUrlBanner, SessionExpiredOverlay } from './components/SessionOverlays';
import InstallPWA from './components/InstallPWA';
import GlobalPaywallListener from './components/GlobalPaywallListener';
import { BadgeCelebration } from './components/BadgeCelebration';
import InAppBrowserBlocker from './components/InAppBrowserBlocker';
import ConsentScreen from './components/ConsentScreen';
import WelcomeScreen from './components/WelcomeScreen';
import UpdateNotification from './components/UpdateNotification';
import BugReportButton from './components/BugReportButton';
import LegalReacceptGate from './components/LegalReacceptGate';
import I18nDebugBanner from './components/I18nDebugBanner';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Landing from './pages/Landing';
import AuthCallback from './pages/AuthCallback';
import Onboarding from './pages/Onboarding';
import Day7ChallengeIntro from './pages/Day7ChallengeIntro';
import DeepChatOnboarding from './pages/DeepChatOnboarding';
import Dashboard from './pages/Dashboard';
import MyGrades from './pages/MyGrades';
import Meals from './pages/Meals';
import Settings from './pages/Settings';
import RecipeBook from './pages/RecipeBook';
import MyRecipes from './pages/MyRecipes';
import MealPlanner from './pages/MealPlanner';
import GroceryDeals from './pages/GroceryDeals';
import BudgetHero from './pages/BudgetHero';
import Presentation from './pages/Presentation';
import DirectLogin from './pages/DirectLogin';
import ForceRefresh from './pages/ForceRefresh';
import Activities from './pages/Activities';
import SOSCraving from './pages/SOSCraving';
import Leaderboard from './pages/Leaderboard';
import Hydration from './pages/Hydration';
import Sleep from './pages/Sleep';
import Mindset from './pages/Mindset';
import MindsetJournal from './pages/MindsetJournal';
import MacrosInsights from './pages/MacrosInsights';
import BadgesGallery from './pages/BadgesGallery';
import FemaleHealth from './pages/FemaleHealth';
import ClinicalTracking from './pages/ClinicalTracking';
import CoachAI from './pages/CoachAI';
import AuthDebug from './pages/AuthDebug';
import Community from './pages/Community';
import KnowledgeBase from './pages/KnowledgeBase';
import About from './pages/About';
import BadgesPage from './pages/BadgesPage';
import BadgeSharePage from './pages/BadgeSharePage';
import WeeklyRecap from './pages/WeeklyRecap';
import MagicLink from './pages/MagicLink';
import WallOfLegends from './pages/WallOfLegends';
import Workouts from './pages/Workouts';
import Supplements from './pages/Supplements';
import GroceryList from './pages/GroceryList';
import Messaging from './pages/Messaging';
import Victories from './pages/Victories';
import WalkingCommand from './pages/WalkingCommand';
import Evolution from './pages/Evolution';
import ClashDesMeutes from './pages/ClashDesMeutes';
import MonthlyReport from './pages/MonthlyReport';
import ClassCensus from './pages/ClassCensus';
import PowerRegister from './pages/PowerRegister';
import AuthEmail from './pages/AuthEmail';
import ForgotPassword from './pages/ForgotPassword';
import WelcomeHome from './pages/WelcomeHome';
import FastingPage from './pages/FastingPage';
import ExtraMilesPage from './pages/ExtraMilesPage';
import PointsHistoryPage from './pages/PointsHistoryPage';
import SafetyShieldTest from './pages/SafetyShieldTest';

// ── Tree-shaking soft: lazy-load admin/coach surfaces (clients never load them) ──
const CoachDashboard = React.lazy(() => import('./pages/CoachDashboard'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminAccessPage = React.lazy(() => import('./pages/AdminAccessPage'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const AlgorithmDashboard = React.lazy(() => import('./pages/AlgorithmDashboard'));
const AdminLegalAudit = React.lazy(() => import('./pages/AdminLegalAudit'));
const AdminScanHealth = React.lazy(() => import('./pages/AdminScanHealth'));
const AdminBugReports = React.lazy(() => import('./pages/AdminBugReports'));
const AdminAIFailures = React.lazy(() => import('./pages/AdminAIFailures'));
const AdminBugRadar = React.lazy(() => import('./pages/AdminBugRadar'));
const Security = React.lazy(() => import('./pages/Security'));
const CrashInspector = React.lazy(() => import('./pages/CrashInspector'));
const CodeHealth = React.lazy(() => import('./pages/admin/CodeHealth'));
const DebugEnv = React.lazy(() => import('./pages/DebugEnv'));

// Protected Route Component
const ProtectedRoute = ({ children, requireOnboarding = true }) => {
  const { user, loading, updateUser, sessionExpired } = useAuth();
  const location = useLocation();
  
  // CRITICAL: Check if user was passed via location.state from AuthCallback
  // This avoids race condition where checkAuth() hasn't completed yet
  React.useEffect(() => {
    if (location.state?.user && location.state?.fromAuth && !user) {
      updateUser(location.state.user);
    }
  }, [location.state, user, updateUser]);
  
  // If we have user from location.state, use it immediately
  const effectiveUser = user || location.state?.user;

  if (loading && !effectiveUser) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  if (!effectiveUser) {
    // If session was already flagged as expired, the SessionExpiredOverlay handles it
    // Just redirect to home cleanly
    if (sessionExpired) {
      return <Navigate to="/" replace />;
    }
    
    // Check if we have a token but user is null (token might be expired or invalid)
    let storedToken = localStorage.getItem('session_token');
    if (!storedToken) {
      storedToken = sessionStorage.getItem('session_token_backup');
      if (storedToken) {
        try {
          localStorage.setItem('session_token', storedToken);
        } catch (e) {
          // Storage blocked
        }
      }
    }
    
    if (storedToken) {
      // Token exists but user is null and loading is done.
      // 🔴 FIX: Do NOT wipe the token here — checkAuth() in AuthProvider is the
      // single source of truth for token lifecycle. Wiping from here caused
      // re-login loops when /auth/me had transient errors (timeout, 5xx).
      // Just redirect to home and let AuthProvider decide.
    }
    
    // No valid session — redirect to login
    if (location.pathname !== '/') {
      sessionStorage.setItem('redirectAfterLogin', location.pathname);
    }
    return <Navigate to="/" replace />;
  }

  // Use effectiveUser for the rest of the checks
  const userToCheck = effectiveUser;

  // SECURITY: Block clients from admin/coach/analytics pages
  // Only 'coach', 'admin', and 'super_admin' roles can access these sensitive areas
  const isPrivilegedUser = userToCheck.role === 'coach' || userToCheck.role === 'admin' || userToCheck.role === 'super_admin';
  const isRestrictedPath = 
    location.pathname.startsWith('/admin') || 
    location.pathname.startsWith('/analytics') ||
    (location.pathname.startsWith('/coach') && !location.pathname.startsWith('/coach-ai'));
  
  if (!isPrivilegedUser && isRestrictedPath) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect client to onboarding if not complete
  if (userToCheck.role === 'client' && !userToCheck.onboarding_complete && requireOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// App Router Component with Consent Blocking
const AppRouter = () => {
  const { user, loading, updateUser } = useAuth();
  const location = useLocation();
  const [consentChecked, setConsentChecked] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [isReconsent, setIsReconsent] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const consentCheckDone = React.useRef(false);

  // Decide whether to show the one-time welcome message.
  // Criteria: logged-in client (not coach/admin), not yet acknowledged in localStorage,
  // and onboarding still pending. Welcome is shown BEFORE the NDA/ConsentScreen.
  useEffect(() => {
    if (!user) {
      setShowWelcome(false);
      return;
    }
    const role = user.role || 'client';
    if (role !== 'client') {
      setShowWelcome(false);
      return;
    }
    if (user.onboarding_complete) {
      setShowWelcome(false);
      return;
    }
    try {
      const seen = localStorage.getItem(`yumix_welcome_seen_${user.user_id}`);
      setShowWelcome(!seen);
    } catch {
      setShowWelcome(true);
    }
  }, [user?.user_id, user?.onboarding_complete, user?.role]);

  // Check consent status when user is authenticated
  useEffect(() => {
    const checkConsent = async () => {
      // Only check once per user session
      if (consentCheckDone.current) return;
      
      if (user) {
        try {
          const response = await api.get('/consent/status');
          if (!response.data.consent_accepted) {
            // Check if this is a re-consent (user had previous version)
            setIsReconsent(response.data.needs_reconsent === true);
            setShowConsent(true);
          } else {
            // Consent already given
            setShowConsent(false);
            consentCheckDone.current = true;
          }
        } catch (error) {
          // If API fails, show consent screen to be safe
          setShowConsent(true);
        }
      }
      setConsentChecked(true);
    };

    if (user) {
      checkConsent();
    } else {
      setConsentChecked(true);
      setShowConsent(false);
      setIsReconsent(false);
      consentCheckDone.current = false;
    }
  }, [user?.user_id]); // Only depend on user_id, not the entire user object

  // CRITICAL: Check URL fragment AND query params for session_id or token synchronously during render
  // This prevents race conditions by processing new session_id/token FIRST before checking existing session_token
  // Emergent Auth may return session_id in hash (#) or query params (?)
  // Also check if we're on the callback page pathname
  if (location.pathname === '/auth/callback' ||
      location.hash?.includes('session_id=') || 
      location.hash?.includes('token=') ||
      location.search?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Show welcome screen as a blocker (one-time, before NDA) for new clients
  if (user && showWelcome) {
    return (
      <WelcomeScreen
        userId={user.user_id}
        userName={user.name}
        onAcknowledge={() => setShowWelcome(false)}
      />
    );
  }

  // Show consent screen as a blocker if needed
  if (user && showConsent) {
    return (
      <ConsentScreen 
        isReconsent={isReconsent}
        onAccepted={() => {
          setShowConsent(false);
          consentCheckDone.current = true;
        }} 
      />
    );
  }

  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      }
    >
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={
          loading ? (
            <div className="min-h-screen bg-background flex items-center justify-center">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
            </div>
          ) : user ? (
            (user.role === 'coach' || user.role === 'super_admin' || user.role === 'admin') ? (
              <Navigate to="/coach" replace />
            ) : user.onboarding_complete ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/onboarding" replace />
            )
          ) : (
            <Landing />
          )
        } 
      />

      {/* Auth Callback - Public */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Safety Shield Test - Public (Zéro Relais validation) */}
      <Route path="/safety-test" element={<SafetyShieldTest />} />

      {/* Security disclosure page — Public (B2B prospect & researcher landing) */}
      <Route path="/security" element={<Security />} />
      
      {/* Email Auth - Public */}
      <Route path="/auth/email" element={<AuthEmail />} />
      {/* Iter. 406.fr.31 — Alias /login → /auth/email (Yuri bug : la
          preview semblait "ne pas marcher" parce que /login renvoyait
          vers la landing au lieu de la page de connexion). */}
      <Route path="/login" element={<Navigate to="/auth/email" replace />} />
      <Route path="/signin" element={<Navigate to="/auth/email" replace />} />
      
      {/* Forgot/Reset Password - Public */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      
      {/* Welcome Home (post-registration) */}
      <Route path="/welcome-home" element={
        <ProtectedRoute requireOnboarding={false}>
          <WelcomeHome />
        </ProtectedRoute>
      } />
      
      {/* Magic Link - Public */}
      <Route path="/activate/:token" element={<MagicLink />} />

      {/* Onboarding */}
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute requireOnboarding={false}>
            <Onboarding />
          </ProtectedRoute>
        } 
      />

      {/* Mandatory Day-1 Vocal Deep Chat — comes before Day-7 intro */}
      <Route
        path="/deep-chat"
        element={
          <ProtectedRoute>
            <DeepChatOnboarding />
          </ProtectedRoute>
        }
      />

      {/* Mandatory Day-7 Challenge Intro after onboarding completion */}
      <Route
        path="/welcome-challenge-intro"
        element={
          <ProtectedRoute>
            <Day7ChallengeIntro />
          </ProtectedRoute>
        }
      />

      {/* Client Dashboard */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />

      {/* Meals */}
      <Route 
        path="/meals" 
        element={
          <ProtectedRoute>
            <Meals />
          </ProtectedRoute>
        } 
      />

      {/* Settings */}
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />

      {/* Coach Dashboard */}
      <Route 
        path="/coach" 
        element={
          <ProtectedRoute>
            <CoachDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Recipe Book */}
      <Route 
        path="/recipes" 
        element={
          <ProtectedRoute>
            <RecipeBook />
          </ProtectedRoute>
        } 
      />

      {/* My Standard Recipes */}
      <Route 
        path="/my-recipes" 
        element={
          <ProtectedRoute>
            <MyRecipes />
          </ProtectedRoute>
        } 
      />

      {/* Meal Planner */}
      <Route 
        path="/planner" 
        element={
          <ProtectedRoute>
            <MealPlanner />
          </ProtectedRoute>
        } 
      />

      {/* Mindset Daily Reflection */}
      <Route
        path="/mindset"
        element={
          <ProtectedRoute>
            <Mindset />
          </ProtectedRoute>
        }
      />

      {/* Iter. 406.fr.35 — Mindset Journal: chronological searchable archive */}
      <Route
        path="/mindset/journal"
        element={
          <ProtectedRoute>
            <MindsetJournal />
          </ProtectedRoute>
        }
      />

      {/* Iter. 406.fr.40 — Macros Insights: Glow chart week/month/year */}
      <Route
        path="/macros/insights"
        element={
          <ProtectedRoute>
            <MacrosInsights />
          </ProtectedRoute>
        }
      />

      {/* Iter. 406.fr.48 — Badges Gallery (milestones + weekly quests + rare) */}
      <Route
        path="/badges"
        element={
          <ProtectedRoute>
            <BadgesGallery />
          </ProtectedRoute>
        }
      />

      {/* Grocery Deals */}
      <Route 
        path="/deals" 
        element={
          <ProtectedRoute>
            <GroceryDeals />
          </ProtectedRoute>
        } 
      />

      {/* Budget Éco */}
      <Route 
        path="/budget-hero" 
        element={
          <ProtectedRoute>
            <BudgetHero />
          </ProtectedRoute>
        } 
      />

      {/* Badges */}
      <Route 
        path="/badges" 
        element={
          <ProtectedRoute>
            <BadgesPage />
          </ProtectedRoute>
        } 
      />

      {/* Admin Dashboard - Coach only */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Admin Legal Audit — ISO 27001 / SOC 2 dashboard — Coach / super_admin only */}
      <Route
        path="/admin/legal-audit"
        element={
          <ProtectedRoute>
            <AdminLegalAudit />
          </ProtectedRoute>
        }
      />

      {/* Admin Scan Health — Opérationnel radar Vision AI — Coach / admin only */}
      <Route
        path="/admin/scan-health"
        element={
          <ProtectedRoute>
            <AdminScanHealth />
          </ProtectedRoute>
        }
      />

      {/* Admin Bug Reports — signalements utilisateurs (Coach / admin only) */}
      <Route
        path="/admin/bug-reports"
        element={
          <ProtectedRoute>
            <AdminBugReports />
          </ProtectedRoute>
        }
      />

      {/* Admin Bug Radar — frontend error telemetry (Coach / admin only) */}
      <Route
        path="/admin/bug-radar"
        element={
          <ProtectedRoute>
            <AdminBugRadar />
          </ProtectedRoute>
        }
      />

      {/* Iter. 406.fk — Surveillance IA (Coach / admin only) */}
      <Route
        path="/admin/ai-failures"
        element={
          <ProtectedRoute>
            <AdminAIFailures />
          </ProtectedRoute>
        }
      />

      {/* Admin Crash Inspector — mini-Sentry interne (local stash + server feed) */}
      <Route
        path="/admin/crash-inspector"
        element={
          <ProtectedRoute>
            <CrashInspector />
          </ProtectedRoute>
        }
      />

      {/* Admin Code Health — dashboard santé des audits CI (Iter. 401) */}
      <Route
        path="/admin/code-health"
        element={
          <ProtectedRoute>
            <CodeHealth />
          </ProtectedRoute>
        }
      />

      {/* Admin Tags d'accès — gestion WORLD/QUEBEC + FREE/PAID/VIP_TESTER */}
      <Route
        path="/admin/access"
        element={
          <ProtectedRoute>
            <AdminAccessPage />
          </ProtectedRoute>
        }
      />

      {/* Clinical Tracking - Coach only */}
      <Route 
        path="/admin/clinic" 
        element={
          <ProtectedRoute>
            <ClinicalTracking />
          </ProtectedRoute>
        } 
      />

      {/* Analytics Dashboard - Coach and Admin only */}
      <Route 
        path="/analytics" 
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        } 
      />

      {/* Leaderboard */}
      <Route 
        path="/leaderboard" 
        element={
          <ProtectedRoute>
            <Leaderboard />
          </ProtectedRoute>
        } 
      />

      {/* Mur des Legendes */}
      <Route 
        path="/legends" 
        element={
          <ProtectedRoute>
            <WallOfLegends />
          </ProtectedRoute>
        } 
      />

      {/* God Mode: Algorithm Dashboard (Coach Only) */}
      <Route 
        path="/admin/algorithm-check" 
        element={
          <ProtectedRoute>
            <AlgorithmDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Hydration Tracker */}
      <Route 
        path="/hydration" 
        element={
          <ProtectedRoute>
            <Hydration />
          </ProtectedRoute>
        } 
      />

      {/* Sleep Tracker */}
      <Route 
        path="/sleep" 
        element={
          <ProtectedRoute>
            <Sleep />
          </ProtectedRoute>
        } 
      />

      {/* Iter. 411.dd — Profile Grade tree (Recrue → Legende, etc.) */}
      <Route
        path="/mes-grades"
        element={
          <ProtectedRoute>
            <MyGrades />
          </ProtectedRoute>
        }
      />

      {/* Assistant YUMIX - Accessible even during onboarding */}
      <Route 
        path="/coach-ai" 
        element={
          <ProtectedRoute requireOnboarding={false}>
            <CoachAI />
          </ProtectedRoute>
        } 
      />

      {/* Community Hub */}
      <Route 
        path="/community" 
        element={
          <ProtectedRoute>
            <Community />
          </ProtectedRoute>
        } 
      />

      {/* Clash des Meutes */}
      <Route 
        path="/clash" 
        element={
          <ProtectedRoute>
            <ClashDesMeutes />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/guide" 
        element={
          <ProtectedRoute>
            <KnowledgeBase />
          </ProtectedRoute>
        } 
      />

      <Route
        path="/female-health"
        element={
          <ProtectedRoute>
            <FemaleHealth />
          </ProtectedRoute>
        }
      />

      <Route
        path="/about"
        element={
          <ProtectedRoute>
            <About />
          </ProtectedRoute>
        }
      />

      <Route 
        path="/recap" 
        element={
          <ProtectedRoute>
            <WeeklyRecap />
          </ProtectedRoute>
        } 
      />

      {/* Workouts / Gym */}
      <Route 
        path="/workouts" 
        element={
          <ProtectedRoute>
            <Workouts />
          </ProtectedRoute>
        } 
      />

      {/* Activities / Multi-Sport */}
      <Route 
        path="/activities" 
        element={
          <ProtectedRoute>
            <Activities />
          </ProtectedRoute>
        } 
      />

      {/* SOS Anti-Craving */}
      <Route 
        path="/sos-craving" 
        element={
          <ProtectedRoute>
            <SOSCraving />
          </ProtectedRoute>
        } 
      />

      {/* Supplements / Mon Protocole */}
      <Route 
        path="/supplements" 
        element={
          <ProtectedRoute>
            <Supplements />
          </ProtectedRoute>
        } 
      />

      {/* Panier Intelligent */}
      <Route 
        path="/grocery-list" 
        element={
          <ProtectedRoute>
            <GroceryList />
          </ProtectedRoute>
        } 
      />

      {/* Messagerie */}
      <Route 
        path="/messaging" 
        element={
          <ProtectedRoute>
            <Messaging />
          </ProtectedRoute>
        } 
      />

      {/* Mes Victoires / Mes Badges */}
      <Route 
        path="/victories" 
        element={
          <ProtectedRoute>
            <Victories />
          </ProtectedRoute>
        } 
      />

      {/* Walking Command Center */}
      <Route 
        path="/walking" 
        element={
          <ProtectedRoute>
            <WalkingCommand />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/evolution" 
        element={
          <ProtectedRoute>
            <Evolution />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/monthly-report" 
        element={
          <ProtectedRoute>
            <MonthlyReport />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/class-census" 
        element={
          <ProtectedRoute>
            <ClassCensus />
          </ProtectedRoute>
        } 
      />

      {/* Power Register / PR Records */}
      <Route 
        path="/power-register" 
        element={
          <ProtectedRoute>
            <PowerRegister />
          </ProtectedRoute>
        } 
      />

      {/* Fasting / Jeune Intermittent */}
      <Route 
        path="/fasting" 
        element={
          <ProtectedRoute>
            <FastingPage />
          </ProtectedRoute>
        } 
      />

      {/* Extra Miles — Steps Tracker & Leaderboard */}
      <Route 
        path="/steps-ranking" 
        element={
          <ProtectedRoute>
            <ExtraMilesPage />
          </ProtectedRoute>
        } 
      />

      {/* Mes Points — Unified gamification history (Extra Miles + Clash + Glory) */}
      <Route
        path="/points-history"
        element={
          <ProtectedRoute>
            <PointsHistoryPage />
          </ProtectedRoute>
        }
      />

      {/* Presentation Mode - Public */}
      <Route path="/presentation" element={<Presentation />} />

      {/* Direct Login - Temporary for debugging */}
      <Route path="/direct-login" element={<DirectLogin />} />
      <Route path="/force-refresh" element={<ForceRefresh />} />
      <Route path="/share/badge/:shareId" element={<BadgeSharePage />} />
      
      {/* Auth Debug - Diagnostic page */}
      <Route path="/auth-debug" element={<AuthDebug />} />

      {/* Env Diagnostic — PUBLIC, no auth required. Helps debug prod
          REACT_APP_BACKEND_URL misconfig on the device. */}
      <Route path="/debug/env" element={<DebugEnv />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
};

// Mount the LegalReacceptGate only for authenticated users. Keeps the gate
// silent on public pages (landing, auth, /safety-test) and guarantees the
// API call runs exactly once after login.
const LegalGateMount = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <LegalReacceptGate user={user} language={user.language || 'fr'} />;
};

// Initialize OneSignal once at app mount and bind external_id when the user
// authenticates. DORMANT until REACT_APP_ONESIGNAL_APP_ID is set in .env, so
// shipping it now is safe (Yuri spec 2026-02 Iter. 317).
const OneSignalLifecycle = () => {
  const { user } = useAuth();
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const { initOneSignal, loginUser, logoutUser } = await import('./utils/onesignal');
      await initOneSignal();
      if (cancelled) return;
      if (user?.user_id) {
        await loginUser(user.user_id, user.email);
      } else {
        await logoutUser();
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, user?.email]);
  return null;
};

function App() {
  const [staleUrl, setStaleUrl] = React.useState(false);
  const [celebrationBadge, setCelebrationBadge] = React.useState(null);
  const badgeQueueRef = React.useRef([]);
  // Global badge celebration listener
  React.useEffect(() => {
    const handleBadgeEarned = (e) => {
      const { badges } = e.detail;
      if (badges && badges.length > 0) {
        badgeQueueRef.current = [...badgeQueueRef.current, ...badges];
        if (!celebrationBadge) {
          setCelebrationBadge(badgeQueueRef.current.shift());
        }
      }
    };
    window.addEventListener('yumix-badge-earned', handleBadgeEarned);
    return () => window.removeEventListener('yumix-badge-earned', handleBadgeEarned);
  }, [celebrationBadge]);

  const handleCloseCelebration = React.useCallback(() => {
    if (badgeQueueRef.current.length > 0) {
      setCelebrationBadge(badgeQueueRef.current.shift());
    } else {
      setCelebrationBadge(null);
    }
  }, []);

  // Iter. 406.fp — Proactive version-mismatch detection. At mount, fetch
  // `/api/version` (lightweight, unauth) and compare its `build_id` against
  // the one we stored on last successful boot. If they differ, the user is
  // running a STALE bundle (cached service-worker, in-app browser cache,
  // PWA shortcut to an old deploy, etc.). We then run the same cleanup +
  // reload pipeline as the crash recovery, but PROACTIVELY and SILENTLY —
  // no error needed to trigger it. First mount ever (no key yet) just
  // stores the build_id and moves on.
  React.useEffect(() => {
    const STORAGE_KEY = 'yumix_build_id';
    const RESET_KEY = '__yumix_version_reset_at';
    const COOLDOWN_MS = 30 * 60 * 1000;
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (!apiBase) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/version`, { cache: 'no-store' });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const remote = data?.build_id;
        if (!remote) return;
        let local;
        try { local = localStorage.getItem(STORAGE_KEY); } catch (_) { return; }
        if (!local) {
          // First boot — just remember the current build_id.
          try { localStorage.setItem(STORAGE_KEY, remote); } catch (_) { /* ignore */ }
          return;
        }
        if (local === remote) return; // up to date
        // MISMATCH — silent cleanup + reload (anti-loop cooldown).
        let last = 0;
        try { last = parseInt(sessionStorage.getItem(RESET_KEY) || '0', 10); } catch (_) { /* ignore */ }
        if (Date.now() - last < COOLDOWN_MS) {
          // Tried recently — just update the stored id and let the user
          // continue with the new bundle they're already on.
          try { localStorage.setItem(STORAGE_KEY, remote); } catch (_) { /* ignore */ }
          return;
        }
        try { sessionStorage.setItem(RESET_KEY, String(Date.now())); } catch (_) { /* ignore */ }
        try { localStorage.setItem(STORAGE_KEY, remote); } catch (_) { /* ignore */ }
        // Unregister all service workers
        try {
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
          }
        } catch (_) { /* ignore */ }
        // Delete all caches
        try {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
        } catch (_) { /* ignore */ }
        // Hard reload — the user sees a brief loading flash, no banner, no
        // "please reinstall" prompt. Completely silent.
        try { window.location.reload(); } catch (_) { /* ignore */ }
      } catch (_) { /* network down or backend cold — fail silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Force Service Worker update on app load
  // Iter. 406.fo — Robust PWA update lifecycle:
  //   1. Re-register the SW with `updateViaCache: 'none'` so the browser
  //      NEVER serves a cached sw.js when checking for updates.
  //   2. When a new worker enters the `waiting` state, ping it with
  //      SKIP_WAITING so it activates immediately (no need to close the
  //      app twice).
  //   3. When `controllerchange` fires (new SW took over), do a SOFT
  //      reload — the next nav already shows the new build.
  //   4. Crash recovery: if anything in the boot path throws and we end
  //      up frozen, the global window.onerror handler below nukes caches
  //      and reloads — never leave the user stuck on a spinner.
  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    let reloaded = false;
    const onControllerChange = () => {
      if (reloaded) return;
      reloaded = true;
      try { window.location.reload(); } catch (_) { /* ignore */ }
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    (async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
        try { await reg.update(); } catch (_) { /* ignore */ }
        const promote = (worker) => {
          if (!worker) return;
          worker.addEventListener('statechange', () => {
            if (worker.state === 'installed' && navigator.serviceWorker.controller) {
              try { worker.postMessage({ type: 'SKIP_WAITING' }); } catch (_) { /* ignore */ }
            }
          });
        };
        if (reg.waiting) {
          try { reg.waiting.postMessage({ type: 'SKIP_WAITING' }); } catch (_) { /* ignore */ }
        }
        if (reg.installing) promote(reg.installing);
        reg.addEventListener('updatefound', () => promote(reg.installing));
      } catch (_) { /* SW unsupported / private mode — fail open */ }
    })();

    return () => {
      try { navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange); } catch (_) { /* ignore */ }
    };
  }, []);

  // Iter. 406.fo — Last-resort crash recovery. If any uncaught error breaks
  // the boot path (corrupted localStorage from a stale build, bad cached
  // bundle, etc.), nuke the SW + caches + suspect localStorage keys and
  // hard-reload ONCE. The `__yumix_reset_at` cookie prevents an infinite
  // loop if the crash isn't actually fixable by a cache wipe.
  React.useEffect(() => {
    const RESET_KEY = '__yumix_reset_at';
    const COOLDOWN_MS = 30 * 60 * 1000; // 30 min
    const recover = async () => {
      try {
        const last = parseInt(sessionStorage.getItem(RESET_KEY) || '0', 10);
        if (Date.now() - last < COOLDOWN_MS) return; // already tried recently
        sessionStorage.setItem(RESET_KEY, String(Date.now()));
        // Clear caches + SW registrations
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
        } catch (_) { /* ignore */ }
        try {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
        } catch (_) { /* ignore */ }
        // Wipe version-mismatch keys but PRESERVE auth/session tokens so the
        // user doesn't get force-logged-out on every crash.
        try {
          const PRESERVE = ['session_token', 'access_token', 'language', 'yumix_stale_url_dismissed_v2'];
          const snapshot = {};
          for (const k of PRESERVE) snapshot[k] = localStorage.getItem(k);
          localStorage.clear();
          for (const [k, v] of Object.entries(snapshot)) if (v != null) localStorage.setItem(k, v);
        } catch (_) { /* ignore */ }
        // Hard reload with cache bypass.
        try { window.location.reload(); } catch (_) { /* ignore */ }
      } catch (_) { /* ignore */ }
    };
    const onError = (event) => {
      const msg = String(event?.error?.message || event?.message || '');
      // Heuristic: chunk-load / module-not-found errors are the typical
      // "stale cached bundle vs new deploy" symptom. Trigger recovery.
      if (/chunk|loading\s+css|loading\s+module|ChunkLoadError|importing/i.test(msg)) {
        recover();
      }
    };
    const onRejection = (event) => {
      const msg = String(event?.reason?.message || event?.reason || '');
      if (/chunk|loading\s+css|loading\s+module|ChunkLoadError|importing/i.test(msg)) {
        recover();
      }
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  // Detect stale URL (PWA installed from old deployment) — non-blocking notice.
  //
  // Iter. 406.fr.68 — Custom-domain safe. Previously the check compared the
  // current origin against REACT_APP_BACKEND_URL as a single source of truth.
  // But YUMIX is served on MULTIPLE valid domains in production:
  //   - extramiles.ca            (custom domain, primary)
  //   - meal-insights-pro.emergent.host  (Emergent-managed)
  //   - preview URLs (during rollout)
  // Users on extramiles.ca were incorrectly told "new version available →
  // uninstall/reinstall" simply because their origin ≠ REACT_APP_BACKEND_URL.
  //
  // The new logic:
  //   1. Extract HOSTNAMES (never compare full URLs — path/protocol vary).
  //   2. An allowlist covers all known valid production hostnames.
  //   3. Iter. 406.fr.70 — RUNTIME HEALTH PROBE: even if the host is unknown,
  //      if the current-origin backend responds to `/api/health/live`, the
  //      deployment is REACHABLE and there is no reason to force a migration.
  //      Runtime truth beats hardcoded allowlist.
  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const expectedUrl = process.env.REACT_APP_BACKEND_URL;
        const currentHost = (window.location.hostname || '').toLowerCase();
        if (!expectedUrl || typeof expectedUrl !== 'string' || !currentHost || currentHost.includes('localhost') || currentHost.startsWith('127.')) return;
        let expectedHost = '';
        try { expectedHost = new URL(expectedUrl).hostname.toLowerCase(); } catch (_) { return; }

        const HARDCODED_ALLOWED = new Set([
          'extramiles.ca',
          'www.extramiles.ca',
          'yumix.ca',
          'www.yumix.ca',
          'meal-insights-pro.emergent.host',
        ]);
        const envRaw = process.env.REACT_APP_ALLOWED_ORIGINS || '';
        const envList = envRaw
          .split(',')
          .map((s) => (s || '').trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '').toLowerCase())
          .filter(Boolean);
        const isAllowedStatic =
          currentHost === expectedHost ||
          HARDCODED_ALLOWED.has(currentHost) ||
          envList.includes(currentHost) ||
          currentHost.endsWith('.preview.emergentagent.com') ||
          currentHost.endsWith('.emergent.host');
        if (isAllowedStatic) return;

        // Respect prior dismissal
        const dismissedFor = localStorage.getItem('yumix_stale_url_dismissed_v2');
        if (dismissedFor === expectedUrl) return;

        // Iter. 406.fr.70 — Runtime health probe. Ping the CURRENT-origin
        // backend. If it answers, the deploy is fine on this host and we
        // suppress the banner (runtime truth > static allowlist).
        try {
          const ctrl = new AbortController();
          const to = setTimeout(() => ctrl.abort(), 3000);
          const res = await fetch(`${window.location.origin}/api/health/live`, {
            method: 'GET',
            cache: 'no-store',
            signal: ctrl.signal,
          });
          clearTimeout(to);
          if (!cancelled && res && res.ok) {
            // Current-origin backend is healthy → this deployment is valid.
            return;
          }
        } catch (_) { /* probe failed → fall through to banner */ }

        if (!cancelled) setStaleUrl(true);
      } catch (_) {
        // localStorage might be unavailable — fail open silently
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Auto-dismiss the stale-url banner after 12s so it never lingers indefinitely
  // if the user ignores it. Iter. 406.fj.
  React.useEffect(() => {
    if (!staleUrl) return undefined;
    const t = setTimeout(() => {
      try { setStaleUrl(false); } catch (_) { /* noop */ }
    }, 12000);
    return () => clearTimeout(t);
  }, [staleUrl]);

  const dismissStaleBanner = React.useCallback(() => {
    try {
      const expectedUrl = process.env.REACT_APP_BACKEND_URL;
      if (expectedUrl) {
        localStorage.setItem('yumix_stale_url_dismissed_v2', expectedUrl);
      }
    } catch (_) { /* private mode safe */ }
    setStaleUrl(false);
  }, []);

  return (
    <ErrorBoundary label="root">
    <InAppBrowserBlocker>
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <FeatureDiscoveryProvider>
          <div className="dark">
            <I18nDebugBanner />
            {staleUrl && <StaleUrlBanner expectedUrl={process.env.REACT_APP_BACKEND_URL} onDismiss={dismissStaleBanner} />}
            <OfflineIndicator language={'fr'} />
            <SessionExpiredOverlay />
            <ErrorBoundary label="router">
              <AppRouter />
            </ErrorBoundary>
            <ErrorBoundary silent label="update-notification">
              <UpdateNotification />
            </ErrorBoundary>
            <ErrorBoundary silent label="bug-report">
              <BugReportButton />
            </ErrorBoundary>
            <ErrorBoundary silent label="legal-gate">
              <LegalGateMount />
            </ErrorBoundary>
            <ErrorBoundary silent label="onesignal">
              <OneSignalLifecycle />
            </ErrorBoundary>
            <Toaster position="top-center" duration={8000} />
            <ErrorBoundary silent label="install-pwa">
              <InstallPWA />
            </ErrorBoundary>
            <ErrorBoundary silent label="paywall-listener">
              <GlobalPaywallListener />
            </ErrorBoundary>
            {celebrationBadge && (
              <BadgeCelebration
                badge={{
                  id: celebrationBadge.badge_id,
                  name: celebrationBadge.name,
                  icon: celebrationBadge.icon,
                  color: celebrationBadge.color,
                  rarity: celebrationBadge.rarity,
                  xp_awarded: celebrationBadge.xp_awarded,
                }}
                onClose={handleCloseCelebration}
                language="fr"
              />
            )}
          </div>
          </FeatureDiscoveryProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
    </InAppBrowserBlocker>
    </ErrorBoundary>
  );
}

export default App;
